import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";
import {
  COUNTRY_CODE_OPTIONS,
  splitPhoneWithCountryCode,
  buildPhoneWithCountryCode,
  getCountryCodeOption
} from "../utils/phoneCountryCodes";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "+220",
  phone: ""
};

const AssetOwners = () => {
  const { toast } = useStore();

  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchOwners = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/asset-owners");
      setOwners(res.data.data || []);
    } catch (error) {
      console.error("Error fetching owners:", error);
      toast("Failed to load asset owners", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const closeForm = () => {
    setForm(emptyForm);
    setSelectedOwner(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedOwner(null);
    setFormOpen(true);
  };

  const openEdit = (owner) => {
    const parsedPhone = splitPhoneWithCountryCode(owner.phone || "", "+220");
    setSelectedOwner(owner);
    setForm({
      firstName: owner.firstName || "",
      lastName: owner.lastName || "",
      email: owner.email || "",
      countryCode: parsedPhone.countryCode,
      phone: parsedPhone.number
    });
    setFormOpen(true);
  };

  const handleAdd = async () => {
    if (!form.firstName || !form.firstName.trim()) {
      toast("First name is required", "error");
      return;
    }

    if (!form.lastName || !form.lastName.trim()) {
      toast("Last name is required", "error");
      return;
    }

    if (!form.phone || !form.phone.trim()) {
      toast("Phone number is required", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email?.trim() || null,
        phone: buildPhoneWithCountryCode(form.countryCode, form.phone) || null
      };

      const response = await api.post("/asset-owners", payload);
      toast(response.data?.message || "Asset owner added successfully", "success");
      closeForm();
      fetchOwners();
    } catch (error) {
      console.error("Error adding owner:", error);
      toast(error.response?.data?.message || "Failed to add asset owner", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedOwner) {
      toast("Please select an owner to update", "error");
      return;
    }

    if (!form.firstName || !form.firstName.trim()) {
      toast("First name is required", "error");
      return;
    }

    if (!form.lastName || !form.lastName.trim()) {
      toast("Last name is required", "error");
      return;
    }

    if (!form.phone || !form.phone.trim()) {
      toast("Phone number is required", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email?.trim() || null,
        phone: buildPhoneWithCountryCode(form.countryCode, form.phone) || null
      };

      await api.put(`/asset-owners/${selectedOwner.id}`, payload);
      toast("Asset owner updated successfully", "success");
      closeForm();
      fetchOwners();
    } catch (error) {
      console.error("Error updating owner:", error);
      toast(error.response?.data?.message || "Failed to update asset owner", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await api.delete(`/asset-owners/${deleteTarget.id}`);
      toast("Asset owner deleted successfully", "success");
      setDeleteTarget(null);
      fetchOwners();
    } catch (error) {
      console.error("Error deleting owner:", error);
      toast("Failed to delete asset owner", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = owners.filter((owner) => {
    const term = search.toLowerCase();
    return (
      (owner.firstName || "").toLowerCase().includes(term) ||
      (owner.lastName || "").toLowerCase().includes(term) ||
      (owner.email || "").toLowerCase().includes(term) ||
      (owner.phone || "").toLowerCase().includes(term)
    );
  });

  const selectedCountry = getCountryCodeOption(form.countryCode);

  return (
    <div>
      <PageHeader
        title="Asset Owners"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Owner
          </Button>
        }
      />

      <DataTable
        columns={[
          { key: "firstName", label: "First Name" },
          { key: "lastName", label: "Last Name" },
          { key: "email", label: "Email", render: (row) => row.email || "-" },
          { key: "phone", label: "Phone Number", render: (row) => row.phone || "-" }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by name, email or phone..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        emptyMessage='No asset owners found. Click "Add Owner" to create one.'
      />

      <Modal
        open={formOpen}
        title={selectedOwner ? "Update Asset Owner" : "Add Asset Owner"}
        onClose={closeForm}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={loading}>
              Cancel
            </Button>
            {selectedOwner ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={LABEL_CLASS}>
              First Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="firstName" value={form.firstName} onChange={handleChange} className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>
              Last Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="lastName" value={form.lastName} onChange={handleChange} className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>Email</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <select name="countryCode" value={form.countryCode} onChange={handleChange} className={FIELD_CLASS}>
                {COUNTRY_CODE_OPTIONS.map((item) => (
                  <option key={item.code} value={item.code}>{item.label}</option>
                ))}
              </select>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Local number"
                className={FIELD_CLASS}
              />
            </div>
            <div className="mt-2">
              <span className="inline-flex items-center rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 border border-teal-200">
                {selectedCountry?.label}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message={deleteTarget ? `Are you sure you want to delete asset owner "${deleteTarget.firstName} ${deleteTarget.lastName}"?` : ""}
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AssetOwners;
